/**
 * useFingeringEditor (issue #22 Phase D)
 *
 * 3 つの hook (Core / Mobile / Desktop) を compose して既存 UseFingeringEditorReturn
 * 契約を維持する wrapper。
 *
 * spec: docs/superpowers/specs/2026-05-24-fingering-editor-3layer-split-design.md §3.3 方式 C
 * plan: docs/superpowers/plans/2026-05-24-fingering-editor-3layer-split.md Phase D
 *
 * Phase A-C で抽出した Core / Mobile / Desktop hook をそのまま呼び、 各 return を merge する。
 * touch / mouse の振り分けは handlers ラッパで pointerType をディスパッチ。
 */
import { MutableRefObject, RefObject } from 'react';
import { useFingeringEditorCore } from './useFingeringEditorCore';
import { useFingeringEditorMobile } from './useFingeringEditorMobile';
import { useFingeringEditorDesktop } from './useFingeringEditorDesktop';
import type { SelectRectState } from './useRangeSelection';
import {
  Badge,
  FingeringData,
  BadgeStatus,
  DragState,
  EditorMode,
  RepeatEditConfirm,
  RepeatEditUpdates,
} from '../types/fingeringEditor';
import { MeasureLine } from '../utils/measureLines';
import type { PendingAction, PdfColorMode, FingeringMode } from '../types/fingering';

// drag 昇格の移動量しきい値 (px)。 既存テスト互換のため named export 維持
// (FingeringEditorPage 経由で再 export されている)。
export const DRAG_THRESHOLD_PX = 5;

export interface PanDragState {
  startClientX: number;
  startClientY: number;
  startPan: { x: number; y: number };
  phase: 'pending' | 'dragging';
  pointerId: number;
}

export interface UseFingeringEditorReturn {
  data: {
    badges: Badge[];
    setBadges: React.Dispatch<React.SetStateAction<Badge[]>>;
    fingeringData: FingeringData | null;
    setFingeringData: React.Dispatch<React.SetStateAction<FingeringData | null>>;
    originalPdfBlob: Blob | null;
    setOriginalPdfBlob: React.Dispatch<React.SetStateAction<Blob | null>>;
    mxlBlob: Blob | null;
    setMxlBlob: React.Dispatch<React.SetStateAction<Blob | null>>;
    badgesHistory: Badge[][];
    setBadgesHistory: React.Dispatch<React.SetStateAction<Badge[][]>>;
    pageBadges: Badge[];
    chordGroups: Map<number, Badge[]>;
    measureLines: MeasureLine[];
    stats: Record<BadgeStatus, number>;
    selected: Badge | null;
    canUndo: boolean;
    // issue #166: 選択/編集した瞬間だけ点る対応バッジ id 集合 (Core から透過)。
    highlightedBadgeIds: ReadonlySet<number>;
  };
  view: {
    zoom: number;
    setZoom: React.Dispatch<React.SetStateAction<number>>;
    pan: { x: number; y: number };
    setPan: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
    zoomOrigin: { x: number; y: number };
    setZoomOrigin: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
    isMobile: boolean;
    setIsMobile: React.Dispatch<React.SetStateAction<boolean>>;
    isPanDragging: boolean;
    setIsPanDragging: React.Dispatch<React.SetStateAction<boolean>>;
    currentPage: number;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    pageCount: number;
    pdfDataUrl: string | null;
    toast: string | null;
    setToast: React.Dispatch<React.SetStateAction<string | null>>;
    loading: boolean;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  };
  mode: {
    editorMode: EditorMode;
    setEditorMode: React.Dispatch<React.SetStateAction<EditorMode>>;
    addMode: boolean;
    setAddMode: React.Dispatch<React.SetStateAction<boolean>>;
    // issue #91 round2 (#44): EditorHeader の part 表示出し分け用。 stored.mode 由来 (欠落時 undefined)。
    storedMode: FingeringMode | undefined;
    // issue #16 Phase 3: selectedId → selectedIds (ReadonlySet) 移行
    selectedIds: ReadonlySet<number>;
    primarySelectedId: number | null;
    selectedCount: number;
    isMultiSelect: boolean;
    // issue #16 v3 Phase A: selectMode は Core hook で editorMode + isMobile の派生値、 setSelectMode は撤去済。
    selectMode: boolean;
    selectRectVisible: SelectRectState | null;
    setSelectRectVisible: (r: SelectRectState | null) => void;
    // 後方互換 (Phase 8 で page 側 destructure 更新後に削除予定)
    selectedId: number | null;
    setSelectedId: (id: number | null) => void;
    panelOpen: boolean;
    setPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  };
  drag: {
    dragBadgeId: number | null;
    setDragBadgeId: React.Dispatch<React.SetStateAction<number | null>>;
    dragDelta: { dx: number; dy: number } | null;
    setDragDelta: React.Dispatch<React.SetStateAction<{ dx: number; dy: number } | null>>;
  };
  edit: {
    chordPickerBeatIndex: number | null;
    setChordPickerBeatIndex: React.Dispatch<React.SetStateAction<number | null>>;
    chordPickerPos: { x: number; y: number };
    setChordPickerPos: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
    detectedBpm: number | undefined;
    setDetectedBpm: React.Dispatch<React.SetStateAction<number | undefined>>;
    currentBpm: number;
    setCurrentBpm: React.Dispatch<React.SetStateAction<number>>;
    tuningChangeOpen: boolean;
    setTuningChangeOpen: React.Dispatch<React.SetStateAction<boolean>>;
    pendingScordatura: string;
    setPendingScordatura: React.Dispatch<React.SetStateAction<string>>;
    // PDF 出力の色モード (issue #32)。Core から透過、setter は state + localStorage persist。
    pdfColorMode: PdfColorMode;
    setPdfColorMode: (mode: PdfColorMode) => void;
    // issue #166: 自動変更バッジ再編集の確認モーダル保留 state (Core から透過)。
    repeatEditConfirm: RepeatEditConfirm | null;
  };
  regenerate: {
    pendingGen: PendingAction | null;
    setPendingGen: React.Dispatch<React.SetStateAction<PendingAction | null>>;
    isGenerating: boolean;
    isGeneratingAudio: boolean;
    // issue #165: 投入前の Drive 再ログインゲート (core.regenerate を丸ごとパススルー)。
    reauthModalOpen: boolean;
    handleReauthConfirm: () => Promise<void>;
    handleReauthCancel: () => void;
  };
  refs: {
    svgRef: RefObject<SVGSVGElement | null>;
    svgWrapperRef: RefObject<HTMLDivElement | null>;
    scrollContainerRef: RefObject<HTMLDivElement | null>;
    dragRef: MutableRefObject<DragState | null>;
    panDragRef: MutableRefObject<PanDragState | null>;
    nextIdRef: MutableRefObject<number>;
    nextBeatIndexRef: MutableRefObject<number>;
    pinchRef: MutableRefObject<{ dist: number; zoom: number }>;
    panStartRef: MutableRefObject<{ x: number; y: number } | null>;
    singleFingerPanStartRef: MutableRefObject<{ x: number; y: number } | null>;
    singleFingerPanOriginRef: MutableRefObject<{ x: number; y: number }>;
    longPressRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
    longPressTriggeredRef: MutableRefObject<boolean>;
    clickSuppressRef: MutableRefObject<boolean>;
    zoomRef: MutableRefObject<number>;
    panRef: MutableRefObject<{ x: number; y: number }>;
    zoomOriginRef: MutableRefObject<{ x: number; y: number }>;
    editorModeRef: MutableRefObject<EditorMode>;
    addModeRef: MutableRefObject<boolean>;
  };
  handlers: {
    showToast: (msg: string) => void;
    pushHistory: () => void;
    handleUndo: () => void;
    resetZoomToInitial: () => void;
    applyPanClamp: (nextPan: { x: number; y: number }) => { x: number; y: number };
    applyZoomWithPivot: (cx: number, cy: number, nextZoom: number) => void;
    clientToSvg: (clientX: number, clientY: number) => { x: number; y: number } | null;
    handleTouchStart: (e: React.TouchEvent) => void;
    handleTouchEnd: () => void;
    dismissPanel: () => void;
    handlePageChange: (page: number) => void;
    handleSvgPointerDown: (e: React.PointerEvent<SVGSVGElement>) => void;
    handleSvgClick: (e: React.MouseEvent<SVGSVGElement>) => void;
    handleBadgeSelect: (id: number, screenX?: number, screenY?: number) => void;
    handleBadgeDragStart: (id: number, e: React.PointerEvent) => void;
    handlePointerMove: (e: React.PointerEvent<SVGSVGElement>) => void;
    handlePointerUp: () => void;
    handlePointerLeave: (e?: React.PointerEvent<SVGSVGElement>) => void;
    addNoteToBeat: (beatIndex: number) => void;
    handleChordNoteSelect: (badgeId: number) => void;
    handleChordAddNote: () => void;
    handleChordPickerClose: () => void;
    handleSave: (
      id: number,
      updates: RepeatEditUpdates,
      options?: { keepPanelOpen?: boolean },
    ) => void;
    // issue #166: 繰り返し編集の確認モーダル分岐 (個別 / 全体)。
    confirmRepeatIndividual: () => void;
    confirmRepeatAll: () => void;
    handleDelete: (id: number) => void;
    // issue #16 Phase 3: 範囲削除 + 選択 API
    handleDeleteMany: (ids: number[]) => void;
    select: (id: number, opts?: { additive?: boolean }) => void;
    toggle: (id: number) => void;
    selectRange: (ids: number[], opts?: { additive?: boolean }) => void;
    clearSelection: () => void;
    handleSaveAndRegenerateAll: () => Promise<void> | void;
    handleGeneratePdfOnly: () => Promise<void> | void;
    handleGenerateAudioOnly: () => Promise<void> | void;
    handleTuningChangeRegenerate: (scordatura: string) => void;
    handleConfirmDiscard: () => Promise<void>;
    handleCancelDiscard: () => void;
    handleToggleAddMode: () => void;
    handleEditorModeChange: (mode: EditorMode) => void;
    handleToolbarZoom: (newZoom: number) => void;
  };
}

export function useFingeringEditor(): UseFingeringEditorReturn {
  const core = useFingeringEditorCore();
  const mobile = useFingeringEditorMobile(core);
  const desktop = useFingeringEditorDesktop(core);

  return {
    data: core.data,
    view: {
      ...core.view,
      isPanDragging: desktop.state.isPanDragging,
      setIsPanDragging: desktop.state.setIsPanDragging,
    },
    mode: core.mode,
    drag: core.drag,
    edit: core.edit,
    regenerate: core.regenerate,
    refs: {
      svgRef: core.refs.svgRef,
      svgWrapperRef: core.refs.svgWrapperRef,
      scrollContainerRef: core.refs.scrollContainerRef,
      dragRef: core.refs.dragRef,
      nextIdRef: core.refs.nextIdRef,
      nextBeatIndexRef: core.refs.nextBeatIndexRef,
      clickSuppressRef: core.refs.clickSuppressRef,
      zoomRef: core.refs.zoomRef,
      panRef: core.refs.panRef,
      zoomOriginRef: core.refs.zoomOriginRef,
      editorModeRef: core.refs.editorModeRef,
      addModeRef: core.refs.addModeRef,
      // Mobile refs
      pinchRef: mobile.refs.pinchRef,
      panStartRef: mobile.refs.panStartRef,
      singleFingerPanStartRef: mobile.refs.singleFingerPanStartRef,
      singleFingerPanOriginRef: mobile.refs.singleFingerPanOriginRef,
      longPressRef: mobile.refs.longPressRef,
      longPressTriggeredRef: mobile.refs.longPressTriggeredRef,
      // Desktop refs
      panDragRef: desktop.refs.panDragRef,
    },
    handlers: {
      // 共通 (Core)
      showToast: core.handlers.showToast,
      pushHistory: core.handlers.pushHistory,
      handleUndo: core.handlers.handleUndo,
      resetZoomToInitial: core.handlers.resetZoomToInitial,
      applyPanClamp: core.handlers.applyPanClamp,
      applyZoomWithPivot: core.handlers.applyZoomWithPivot,
      clientToSvg: core.handlers.clientToSvg,
      dismissPanel: core.handlers.dismissPanel,
      handlePageChange: core.handlers.handlePageChange,
      handleSvgClick: core.handlers.handleSvgClick,
      handleBadgeSelect: core.handlers.handleBadgeSelect,
      addNoteToBeat: core.handlers.addNoteToBeat,
      handleChordNoteSelect: core.handlers.handleChordNoteSelect,
      handleChordAddNote: core.handlers.handleChordAddNote,
      handleChordPickerClose: core.handlers.handleChordPickerClose,
      handleSave: core.handlers.handleSave,
      // issue #166: 繰り返し編集の確認モーダル分岐 (Core から透過 expose)
      confirmRepeatIndividual: core.handlers.confirmRepeatIndividual,
      confirmRepeatAll: core.handlers.confirmRepeatAll,
      handleDelete: core.handlers.handleDelete,
      // issue #16 Phase 3: 範囲削除 + 選択 API (Core から透過 expose)
      handleDeleteMany: core.handlers.handleDeleteMany,
      select: core.handlers.select,
      toggle: core.handlers.toggle,
      selectRange: core.handlers.selectRange,
      clearSelection: core.handlers.clearSelection,
      handleSaveAndRegenerateAll: core.handlers.handleSaveAndRegenerateAll,
      handleGeneratePdfOnly: core.handlers.handleGeneratePdfOnly,
      handleGenerateAudioOnly: core.handlers.handleGenerateAudioOnly,
      handleTuningChangeRegenerate: core.handlers.handleTuningChangeRegenerate,
      handleConfirmDiscard: core.handlers.handleConfirmDiscard,
      handleCancelDiscard: core.handlers.handleCancelDiscard,
      handleToggleAddMode: core.handlers.handleToggleAddMode,
      handleToolbarZoom: core.handlers.handleToolbarZoom,
      // Mobile 専用
      handleTouchStart: mobile.handlers.handleTouchStart,
      handleTouchEnd: mobile.handlers.handleTouchEnd,
      handleEditorModeChange: mobile.handlers.handleEditorModeChange,
      // Desktop 専用
      handleSvgPointerDown: desktop.handlers.handleSvgPointerDown,
      // ハイブリッド (pointerType でディスパッチ)
      // pointerType === 'mouse' のみ Desktop、 それ以外 (touch / pen / 空文字 (jsdom)) は Mobile に振り分ける。
      // 元コードでは pointerType を区別せず単一の handler で処理していたため、 空文字 (jsdom テスト) も
      // Mobile 側のロジックで吸収する。 PC mouse での badge drag は spec §2.3 注釈に従い実用上は不要。
      handleBadgeDragStart: (id, e) => {
        if (e.pointerType === 'mouse') desktop.handlers.handleBadgeDragStart(id, e);
        else mobile.handlers.handleBadgeDragStart(id, e);
      },
      handlePointerMove: (e) => {
        if (e.pointerType === 'mouse') desktop.handlers.handlePointerMove(e);
        else mobile.handlers.handlePointerMove(e);
      },
      handlePointerUp: () => {
        // mobile / desktop それぞれ pointerType ガード or panDragRef ガード内で no-op
        mobile.handlers.handlePointerUp();
        desktop.handlers.handlePointerUp();
      },
      handlePointerLeave: (e) => {
        mobile.handlers.handlePointerLeave(e);
        desktop.handlers.handlePointerLeave(e);
      },
    },
  };
}
